import {absoluteUrl, hreflangAlternatesFor, SITE_LOCALES, SITE_ORIGINS, type SiteLocale} from "seo/siteMeta";
import {SITEMAP_ROUTES} from "seo/sitemap";
import {treeRelativePath} from "seo/documentLang";

/**
 * Where each language's copy of the page the reader is on actually lives (#1545).
 *
 * **Why the language switcher needs this at all.** The two brands are separate origins —
 * transferify.cloud publishes English, transferify.ro Romanian — and until now the only statement of
 * that relationship a crawler could find was the `hreflang` pair in `<head>`. hreflang passes no link
 * value, so the .ro site, which has almost no external links, was getting nothing from the older
 * property either. Giving the switcher real `href`s puts the relationship in the page's link graph.
 *
 * **What it can answer.** The hand-kept routes in {@link SITEMAP_ROUTES} — home, pricing, the
 * explainer pages, the policies — are the pages both trees ship, and the reciprocal URL pair comes
 * straight from {@link hreflangAlternatesFor}, the same function the sitemap and `<Seo>` derive their
 * alternates from. Anything else (a markdown content page with a localized slug, a private app route)
 * resolves to that language's home page: the corpus pairing is build-time data the nav shell does not
 * carry, and a link to the other brand's home is true, whereas a guessed slug would be a 404.
 *
 * **And where it must answer NOTHING.** A claimed or BYO customer host serves the recipient shell and
 * nothing else (ui#432, devops#137). A cross-brand anchor there does not point at another page of the
 * site the reader is on — it points OFF the customer's domain, onto ours, from a page their own
 * recipient opened. That is the rule devops#142 exists for, in its sharpest form, and it is why
 * {@link languageAlternateUrls} takes the host answer and returns `null` rather than leaving the
 * decision to whichever component renders the rows.
 *
 * React-free (the `logic-outside-react` rule) — {@link ./LanguageRows.tsx} is a thin caller.
 */

/**
 * The absolute URL of this page in each language, or `null` where no cross-brand link may be offered.
 *
 * @param pathname `useLocation().pathname`. Both addresses of a Romanian page answer identically:
 *   `/ro/pricing` (the mirror the English host pre-renders) and `/pricing` (what the browser sees on
 *   transferify.ro) collapse to the same tree-relative path, which is what keeps the pre-rendered
 *   HTML and the hydrated render byte-identical.
 * @param siteLinkOrigin {@link ../nav/siteLinkOrigin.siteLinkOrigin}'s answer for the serving host:
 *   `null` when this host serves the whole site, and the brand origin when it does not — a claimed
 *   subdomain or a BYO custom domain. Non-null therefore means "recipient surfaces only", and this
 *   returns `null` so no anchor is rendered at all.
 *
 *   The pre-render and the first client render both pass `null` (there is no host to read yet), which
 *   is what keeps the crawlable anchors in the emitted HTML while a claimed host drops them the moment
 *   it mounts.
 */
export function languageAlternateUrls(
	pathname: string,
	siteLinkOrigin: string | null,
): Record<SiteLocale, string> | null {
	if (siteLinkOrigin !== null) {
		return null;
	}
	const path = treeRelativePath(pathname);
	// The path arrives tree-relative, so it matches whichever locale's slug the reader is on — a
	// bilingual content page has a different one per language, and any of them identifies the route.
	const route = SITEMAP_ROUTES.find((candidate) =>
		SITE_LOCALES.some((locale) => candidate.paths[locale] === path));
	const alternates = hreflangAlternatesFor(route?.paths ?? {});
	const urlFor = (locale: SiteLocale): string => {
		// Matched on the alternate's LOCALE, not on its `hreflang` tag: since ui#2471 the tags are BCP-47
		// (`ro-RO`, `de-DE`) and comparing a tag to a locale code silently matched nothing, which here
		// reads as "this page has no Romanian counterpart" and sends the reader to the other brand's home.
		const alternate = alternates.find((candidate) => candidate.locale === locale);
		return alternate === undefined
			? absoluteUrl(SITE_ORIGINS[locale], "/")
			: absoluteUrl(alternate.origin, alternate.path);
	};
	return Object.fromEntries(SITE_LOCALES.map((locale) => [locale, urlFor(locale)])) as Record<SiteLocale, string>;
}
